/**
 * ReviewGrid — the plan review grid (toolbar, column-group tabs, resizable
 * table with cell annotations, flag popover, and the cell detail panel).
 *
 * Extracted verbatim from CampaignLaunchWorkflow's ReviewPane so the Launch
 * QA page can render the identical grid instead of a parallel implementation.
 * Behaviour is unchanged; only the surrounding wiring became props.
 *
 * MUST be rendered inside an element carrying the `clw` class — every colour
 * in CampaignLaunchWorkflow.css is a custom property scoped to `.clw`.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { CellDetailPanel } from './CellDetailPanel';
import { TacticPill } from '../tacticBadge';
import { fmtCellValue, fmtBadgeClass } from './clwFormatters';
import { buildGtReviewAuditSheets } from './groundtruth/gtReviewExport';
import { buildReviewGroupSheet } from './reviewExcelExport';

// Zero-height and invisible but still laid out, so scroll offsets survive.
const PARKED_STYLE = { height: 0, minHeight: 0, overflow: 'hidden', visibility: 'hidden', padding: 0, border: 0 };

// Local copy of CampaignLaunchWorkflow's module-level helper (not exported there).
const fmtInt = (n) => Number(n || 0).toLocaleString();

/**
 * Filename stem for the Excel export.
 *
 * The same uploaded document must always export under the same name (the
 * trailing timestamp aside). That rules out every model-derived candidate:
 * campaign.salesForce_opportunity_name varies between runs of one PDF, both
 * from sampling (`3D_Walmart` vs `3D Walmart`) and — structurally — because
 * it embeds the *normalised* flight dates, so re-running next week renames
 * the file. A hand-typed campaign name drifts the same way (one Keratime PDF
 * produced both `..._Q3_2026_0812` and `..._Launch_Q3_2026`). The uploaded
 * filename is the only stem that is stable by construction.
 *
 * Accepts either a bare filename or a full GCS key/URI: a resumed review can
 * carry a gcsPath with no originalName, because the cts snapshot persists
 * `originalName: sourceFile.originalName || null` (routes/v2/campaigns.js).
 * The key's last segment is the uploaded filename, which is the same fallback
 * the geofence source-file copy already relies on.
 *
 * Steps are ordered deliberately: whitespace first, then the remaining
 * disallowed characters, then collapse, then trim, and only then fall back.
 * Trimming before the fallback is what keeps a name of pure punctuation from
 * re-emerging as `_review_<ts>.xlsx`.
 */
export const exportFileBase = (filename) => (
  String(filename || '')
    .split('/').pop()                   // tolerate a gs:// URI or bucket key
    // Known upload extensions only -- the same list handleLaunch uses for its
    // display stem. A greedy /\.[^.]+$/ would eat the trailing segment of a
    // dotted date (`..._7.15.26` -> `..._7.15`) on any name lacking one.
    .replace(/\.(xlsx|xls|xlsm|pdf|zip|csv|json)$/i, '')
    .replace(/\s+/g, '_')               // whitespace -> _
    .replace(/[^A-Za-z0-9._-]+/g, '_')  // anything else Excel/OS dislikes -> _
    .replace(/_+/g, '_')                // collapse runs
    .replace(/^_+|_+$/g, '')            // trim edges
) || 'campaign';

export default function ReviewGrid({
  // data
  reviewRows, visibleRows, getRowValue, editedRows,
  groupCols, groupKeys, cols, effectiveActiveGroup, isGt,
  // view state (controlled by the host so deep-links / jump-to-cell work)
  activeGroup, setActiveGroup, gridSearch, setGridSearch,
  filterIssuesOnly, setFilterIssuesOnly,
  // cell detail + editing
  detailRow, detailCellField, setDetailRowId, setDetailCellField,
  editingCell, setEditingCell, editingDraft, setEditingDraft,
  commitCellEdit, cancelCellEdit, editableFields, fieldEditors,
  // Fixed option sets for 'select' editors — see CellScalarEditor.
  fieldOptions = null,
  // GT IO review "Upload Creatives": when the host passes { onFile, busy,
  // disabled, label }, the toolbar offers a CTS .xlsx / .zip picker. The host
  // decides applicability (GT IO PDF reviews only); others pass nothing.
  creativeUpload = null,
  // GT IO review "Remove Creatives": { onRemove, busy, disabled } while the plan
  // carries an uploaded bundle; the host confirms and saves.
  creativeRemove = null,
  // Per-field read-only reasons — see CellScalarEditor.
  readOnlyReasons = null,
  ctsReview, pendingDeletions, toggleListDeletion, alreadyLaunched,
  // list add/edit support (optional — hosts without it keep remove-only)
  listFields = {}, pendingListOps = {}, addListItem, editListItem,
  // Async resolver for lookup-backed list adds: (kind, query) => Promise<rows>.
  // Only the hosts that know the campaign id can supply it.
  onLookup,
  // Optional GT taxonomy recovery: () => Promise — fetch the account's
  // taxonomy live and cache it to GCS. Passed through to the cell picker.
  onRefreshLookup,
  // toolbar
  campaignName, aiOpen, setAiOpen,
  // Original name of the uploaded source document, when the host knows it.
  // Preferred over campaignName for the export filename because it cannot
  // drift for a given upload; hosts without a source file (the Google Ads
  // copy grid) keep the campaignName-based naming.
  exportSourceName = '',
  // sessionStorage namespace for persisted column widths
  widthsKey,
  // Sticky column header labels. Defaults preserve the generic launch grid
  // (row-index column unlabeled, sticky name column "Name"); the Google Ads copy
  // grid overrides them to "Order" / "Campaign" since its sticky column is the
  // campaign name.
  stickyF1Label = '', stickyF2Label = 'Name',
  // CLW-only breakdown tab: omit both on other surfaces
  showBreakdownTab = false, breakdownSlot = null,
  // Row removal (copy grid only — omit it and the row-index column renders
  // exactly as before). `(rowId) => void`; the host owns the template edit and
  // the save. Two clicks are required: the first arms the row, the second
  // confirms, because removing a child is not recoverable from this grid.
  onRemoveRow = null, removeRowTitle = 'Remove this row from the copy',
  // ── Row multi-select (optional) ─────────────────────────────────────────
  // Pass a Set of rowIds + a setter to turn on the checkbox column; omit
  // `selectedRowIds` entirely and the grid renders exactly as it always has.
  // The host owns the Set because the selection outlives this component (it is
  // sent to the bulk-edit agent as a scope) and has to be pruned when the rows
  // are rebuilt — on GroundTruth and Google Ads rowIds are positional, so a
  // selection held across a save can point at different rows.
  selectedRowIds = null,
  onSelectionChange = null,
  // Bulk row removal (copy grid only). `(rowIds: string[]) => void`, rendered
  // beside the selection readout because that is what the selection is for
  // here. Two clicks like the per-row ×: removing children is not recoverable
  // from this grid, and a bulk one is that much less so. Omit it and the
  // readout renders exactly as before.
  onRemoveRows = null,
  // ── Sibling-group select prompt (optional) ──────────────────────────────
  // The DV360 copy grid's rows are line items, but its real unit of work is
  // the INSERTION ORDER: someone dropping one line item from an order is
  // usually dropping the order, and removeCopyRow refuses to leave an IO
  // childless — so picking them one at a time ends in a refusal on the last
  // one. Name the row field that identifies the group and checking a single
  // row offers the rest of its group. Omit it (every other host does) and
  // selection behaves exactly as it always has: no prompt, no extra state.
  siblingGroupField = null,
  // Row field holding the group's human name, spoken in the prompt.
  siblingGroupLabelField = null,
  // What the group IS, e.g. 'insertion order', and what its members are called.
  siblingGroupNoun = 'group',
  siblingGroupItemNoun = 'row',
  // Optional second line in the prompt — why taking the whole group is the
  // useful answer on this surface.
  siblingGroupNote = null,
  // ── Toolbar affordances ─────────────────────────────────────────────────
  // All ON by default, so every existing host is unchanged. A host turns one
  // off when it doesn't apply to its surface: the copy grid hides all three
  // (its rows are a plan being confirmed, not placements being triaged).
  showSearch = true,
  showIssuesFilter = true,
  showAgentButton = true,
  // slots rendered inside .clw-grid-inner (agent sidebar) and below it
  children = null, summaryBar = null,
  // A node shown in place of the toolbar, tabs and table — the minified
  // campaign detail page. `children` (the agent sidebar) still render.
  replaceGrid = null,
  // ── Trailing view tab (optional) ────────────────────────────────────────
  // { label, active, onSelect, onLeave } adds a tab after the column groups
  // that switches the HOST's view (the launched GT workspace's Activity log).
  // While it is active the toolbar and table stay mounted but parked, so the
  // grid's tab, search, filter and scroll survive the round trip; picking a
  // column group calls onLeave. Omit it and the grid renders as before.
  trailingTab = null,
}) {
  // rowId armed for removal (first click). Cleared on confirm or cancel.
  const [armedRemoveRowId, setArmedRemoveRowId] = useState(null);
  // Same two-step arming for the selection-wide removal. Disarmed whenever the
  // selection empties, so a later selection never opens already-armed.
  const [armedBulkRemove, setArmedBulkRemove] = useState(false);
  const selectionSize = selectedRowIds ? selectedRowIds.size : 0;
  // Upload Creatives is off while it runs, and while Remove Creatives does.
  const uploadOff = Boolean(creativeUpload?.busy || creativeUpload?.disabled);
  // The pending "take the rest of this group too?" offer, or null. Retired
  // alongside the bulk arming when the selection empties: an offer whose
  // opening click has since been cleared is answering a question nobody asked.
  const [groupPrompt, setGroupPrompt] = useState(null);
  useEffect(() => {
    if (selectionSize === 0) { setArmedBulkRemove(false); setGroupPrompt(null); }
  }, [selectionSize]);
  // ── Column width state (drag-resizable). Keyed by column key so widths
  //    survive group-tab switches. Loaded/saved from sessionStorage.
  const [colWidths, setColWidths] = useState(() => {
    try {
      const raw = sessionStorage.getItem(widthsKey);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  // Sticky columns also resizable.
  const [stickyWidths, setStickyWidths] = useState(() => {
    try {
      const raw = sessionStorage.getItem(widthsKey + '_sticky');
      return raw ? JSON.parse(raw) : { f1: 52, f2: 220 };
    } catch { return { f1: 52, f2: 220 }; }
  });
  const persistWidths = useCallback((next) => {
    try { sessionStorage.setItem(widthsKey, JSON.stringify(next)); } catch {}
  }, [widthsKey]);
  const persistStickyWidths = useCallback((next) => {
    try { sessionStorage.setItem(widthsKey + '_sticky', JSON.stringify(next)); } catch {}
  }, [widthsKey]);
  // Generic drag-resize handler. `target` = 'col' | 'sticky'. `key` = column
  // key (for 'col') or 'f1'/'f2' (for 'sticky').
  const startResize = useCallback((e, target, key, startWidth) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    // Looser minimums than the design's 60/32 so users can crush rarely-used
    // columns down for more horizontal real estate. The visible cell still
    // ellipsis-truncates so nothing breaks at these widths.
    const minWidth = target === 'sticky' && key === 'f1' ? 24 : 40;
    const handleEl = e.currentTarget;
    handleEl.classList.add('dragging');
    document.body.classList.add('clw-col-resizing');
    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const next = Math.max(minWidth, Math.round(startWidth + dx));
      if (target === 'col') {
        setColWidths((prev) => {
          const updated = { ...prev, [key]: next };
          persistWidths(updated);
          return updated;
        });
      } else {
        setStickyWidths((prev) => {
          const updated = { ...prev, [key]: next };
          persistStickyWidths(updated);
          return updated;
        });
      }
    };
    const onUp = () => {
      handleEl.classList.remove('dragging');
      document.body.classList.remove('clw-col-resizing');
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [persistWidths, persistStickyWidths]);

  // ── Selection ───────────────────────────────────────────────────────────
  const selectionOn = selectedRowIds instanceof Set && typeof onSelectionChange === 'function';
  // Anchor for shift-click range selection: the last row toggled on its own.
  const rangeAnchor = useRef(null);
  const isSelected = (rowId) => selectionOn && selectedRowIds.has(String(rowId));
  const visibleSelectedCount = selectionOn
    ? visibleRows.reduce((n, r) => n + (selectedRowIds.has(String(r.rowId)) ? 1 : 0), 0)
    : 0;
  const allVisibleSelected = selectionOn && visibleRows.length > 0
    && visibleSelectedCount === visibleRows.length;

  // The rows sharing `id`'s group that are NOT in `selection` — the offer to
  // put to the user, or null when there is nothing left to offer. `selection`
  // is the Set as it will be AFTER the click, so the row just checked is
  // already excluded. Scoped to visibleRows for the same reason the header
  // checkbox is: offering rows a filter is hiding selects what nobody saw.
  const pendingGroupFor = useCallback((id, selection) => {
    if (!siblingGroupField) return null;
    const me = visibleRows.find((r) => String(r.rowId) === id);
    const key = me ? me[siblingGroupField] : null;
    if (key == null || key === '') return null;
    const ids = visibleRows
      .filter((r) => r[siblingGroupField] === key && !selection.has(String(r.rowId)))
      .map((r) => String(r.rowId));
    if (ids.length === 0) return null;
    return {
      ids,
      rowName: me.name || null,
      groupLabel: (siblingGroupLabelField && me[siblingGroupLabelField]) || null,
    };
  }, [siblingGroupField, siblingGroupLabelField, visibleRows]);

  const toggleRow = useCallback((rowId, shiftKey) => {
    if (!selectionOn) return;
    const id = String(rowId);
    const next = new Set(selectedRowIds);
    // Shift-click extends from the last single toggle to here, over the rows as
    // CURRENTLY FILTERED — which is what "everything between these two" means to
    // someone looking at a filtered grid.
    if (shiftKey && rangeAnchor.current !== null) {
      const ids = visibleRows.map((r) => String(r.rowId));
      const from = ids.indexOf(rangeAnchor.current);
      const to = ids.indexOf(id);
      if (from !== -1 && to !== -1) {
        const [lo, hi] = from <= to ? [from, to] : [to, from];
        // Extending always ADDS. A shift-click that cleared part of the range
        // would silently undo an earlier selection the user cannot see.
        for (let i = lo; i <= hi; i += 1) next.add(ids[i]);
        // A range already says which rows the user means; no offer on top of it.
        setGroupPrompt(null);
        onSelectionChange(next);
        return;
      }
    }
    if (next.has(id)) {
      next.delete(id);
      setGroupPrompt(null);
    } else {
      next.add(id);
      setGroupPrompt(pendingGroupFor(id, next));
    }
    rangeAnchor.current = id;
    onSelectionChange(next);
  }, [selectionOn, selectedRowIds, onSelectionChange, visibleRows, pendingGroupFor]);

  const toggleAllVisible = useCallback(() => {
    if (!selectionOn) return;
    const next = new Set(selectedRowIds);
    if (allVisibleSelected) {
      // Clears only what is on screen: rows hidden by the current filter stay
      // selected, because the user never saw them to deselect them.
      for (const r of visibleRows) next.delete(String(r.rowId));
    } else {
      for (const r of visibleRows) next.add(String(r.rowId));
    }
    rangeAnchor.current = null;
    setGroupPrompt(null);
    onSelectionChange(next);
  }, [selectionOn, selectedRowIds, onSelectionChange, visibleRows, allVisibleSelected]);

  // Yes → the offered rows join the selection. Re-filtered against the live
  // selection and the live rows first: the grid can have rebuilt (a save lands
  // between the click and the answer) and the offer must not resurrect a rowId
  // that no longer exists.
  const acceptGroupPrompt = useCallback(() => {
    if (!groupPrompt || !selectionOn) { setGroupPrompt(null); return; }
    const live = new Set(visibleRows.map((r) => String(r.rowId)));
    const next = new Set(selectedRowIds);
    for (const id of groupPrompt.ids) if (live.has(id)) next.add(id);
    setGroupPrompt(null);
    // Range-extending from a group accept would span the whole group, which is
    // not what the user pointed at — the anchor stays on the row they clicked.
    onSelectionChange(next);
  }, [groupPrompt, selectionOn, selectedRowIds, onSelectionChange, visibleRows]);

  const widthOf = (col) => colWidths[col.k] ?? col.w ?? 120;
  // The checkbox needs room the index column was never sized for, and f1 widths
  // are PERSISTED — an existing user has 52px stored, which would clip. Floor the
  // rendered width instead of rewriting their stored preference: they can still
  // widen it, just not crush it below what selection needs.
  const f1Width = selectionOn
    ? Math.max(72, stickyWidths.f1 ?? 52)
    : (stickyWidths.f1 ?? 52);
  const f2Left = f1Width;
  // Clicking a cell's !/? marker opens a small popover anchored to it, showing
  // what the plan supplied and what the validator needs — a lighter read than
  // opening the full detail panel. { rowId, field, ann, colLabel, raw, x, y }.
  const [flagPopover, setFlagPopover] = useState(null);
  // "rowId::field" for annotations the user has dismissed as expected. Local to
  // the session: the plan is unchanged, so a re-validate brings them back.
  const [dismissedAnns, setDismissedAnns] = useState(() => new Set());

  // Parked, not unmounted: the host's other view is showing below the tabs.
  const parked = trailingTab?.active ? PARKED_STYLE : undefined;

  if (replaceGrid) {
    return (
      <div className="clw-grid-wrap clw-grid-wrap--replaced">
        <div className="clw-grid-inner">
          {replaceGrid}
          {children}
        </div>
      </div>
    );
  }

  return (
      <div className="clw-grid-wrap">
        <div className="clw-gtoolbar" style={parked}>
          <button
            className="clw-btn clw-btn-secondary clw-btn-sm"
            onClick={async () => {
              // Build an .xlsx workbook with one sheet per column group
              // (overview, flighting, budget, targeting, …). The first
              // column on every sheet is the Ad group / Line item name
              // so each tab can be read standalone. Values pass through
              // getRowValue so pending edits are reflected. Filters
              // (search + issues-only) apply to all sheets. Flagged cells
              // are filled red (error) / yellow (warning), as the grid tints them.
              try {
                const XLSX = (await import('xlsx-js-style')).default || (await import('xlsx-js-style'));
                const wb = XLSX.utils.book_new();
                const annotationOf = (row, field) => (
                  dismissedAnns.has(`${row.rowId}::${field}`) ? null : row.annotations?.[field]);
                for (const g of groupKeys) {
                  const gCols = groupCols[g] || [];
                  if (gCols.length === 0) continue;
                  const ws = buildReviewGroupSheet(XLSX, {
                    cols: gCols, rows: visibleRows, getRowValue, annotationOf,
                  });
                  // Sheet name capped at 31 chars (Excel limit) and stripped
                  // of characters Excel forbids in tab names.
                  const sheetName = String(g).slice(0, 31).replace(/[\\/?*[\]:]/g, '_');
                  XLSX.utils.book_append_sheet(wb, ws, sheetName);
                }
                const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
                if (isGt) {
                  for (const sheet of buildGtReviewAuditSheets({ ctsReview, visibleRows, editedRows,
                    pendingListOps, pendingDeletions })) {
                    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sheet.rows), sheet.name);
                  }
                }
                const safeCampaign = exportFileBase(exportSourceName || campaignName);
                XLSX.writeFile(wb, `${safeCampaign}_review_${ts}.xlsx`);
              } catch (err) {
                console.error('Export Excel failed', err);
                alert(`Couldn't export Excel: ${err.message}`);
              }
            }}
            disabled={visibleRows.length === 0}
            title="Export all column groups as separate tabs in a single .xlsx"
          >↓ Export Excel</button>
          <div className="spacer" />
          {showSearch && (
            <input
              className="clw-gsearch"
              placeholder="🔍 Filter placements…"
              value={gridSearch}
              onChange={(e) => setGridSearch(e.target.value)}
            />
          )}
          {/* Issues-only toggle. The button now:
                - Reports the issue count inline so reviewers know how many
                  rows match before they click ('Show 5 issues only').
                - Gets an explicit 'active' className when the filter is
                  engaged so the visual state matches the toggle state.
                - Disables itself when there are zero annotated rows so
                  clicking it doesn't appear to be a no-op. */}
          {showIssuesFilter && (() => {
            const issueRowCount = reviewRows.reduce(
              (n, r) => n + (Object.keys(r.annotations || {}).length > 0 ? 1 : 0),
              0,
            );
            const noIssues = issueRowCount === 0;
            const label = filterIssuesOnly
              ? 'Show all placements'
              : `Show issues only${issueRowCount > 0 ? ` (${fmtInt(issueRowCount)})` : ''}`;
            return (
              <button
                className={`clw-btn clw-btn-navy clw-btn-sm${filterIssuesOnly ? ' active' : ''}`}
                onClick={() => setFilterIssuesOnly((v) => !v)}
                disabled={noIssues && !filterIssuesOnly}
                title={
                  noIssues
                    ? 'No annotated rows to filter — everything is clean.'
                    : (filterIssuesOnly ? 'Show every row again' : `Filter to the ${issueRowCount} row${issueRowCount === 1 ? '' : 's'} with annotations`)
                }
              >
                {label}
              </button>
            );
          })()}
          {/* Selection readout. Sits next to the agent button because that is
              what the selection is FOR — it scopes the bulk editor — and because
              a count the user cannot see is a count they will forget they set. */}
          {selectionOn && selectedRowIds.size > 0 && (
            <>
              <div className="clw-gtoolbar-sep" />
              <span className="clw-selcount" title="These rows scope what the bulk editor changes">
                <strong>{fmtInt(selectedRowIds.size)}</strong> selected
                <button
                  type="button"
                  className="clw-selclear"
                  onClick={() => { setArmedBulkRemove(false); onSelectionChange(new Set()); }}
                  title="Clear the selection — the agent goes back to the whole plan"
                >✕</button>
              </span>
              {onRemoveRows && (armedBulkRemove ? (
                <span className="clw-selbulk-confirm">
                  <button
                    type="button"
                    className="clw-btn clw-btn-danger clw-btn-sm"
                    onClick={() => {
                      setArmedBulkRemove(false);
                      onRemoveRows([...selectedRowIds]);
                    }}
                  >Remove {fmtInt(selectedRowIds.size)} — confirm</button>
                  <button
                    type="button"
                    className="clw-btn clw-btn-secondary clw-btn-sm"
                    onClick={() => setArmedBulkRemove(false)}
                  >Cancel</button>
                </span>
              ) : (
                <button
                  type="button"
                  className="clw-btn clw-btn-secondary clw-btn-sm clw-selbulk"
                  onClick={() => setArmedBulkRemove(true)}
                  title={`Remove the ${selectedRowIds.size} selected row${selectedRowIds.size === 1 ? '' : 's'} from the copy`}
                >✕ Remove selected</button>
              ))}
            </>
          )}
          {(creativeUpload || creativeRemove) && <div className="clw-gtoolbar-sep" />}
          {creativeUpload && (
            <label
              className={`clw-btn clw-btn-secondary clw-btn-sm${uploadOff ? ' disabled' : ''}`}
              title="Upload the client CTS workbook (.xlsx) or a .zip with the workbook and its creative assets — creatives, tags, trackers and pixels are added to this plan for review. Nothing is created in GroundTruth until you approve."
              style={uploadOff ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
              data-testid="gt-upload-creatives"
            >
              {creativeUpload.busy ? (creativeUpload.label || 'Uploading creatives…') : '⬆ Upload Creatives'}
              <input
                type="file"
                accept=".xlsx,.zip"
                style={{ display: 'none' }}
                disabled={uploadOff}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) creativeUpload.onFile(f);
                }}
              />
            </label>
          )}
          {creativeRemove && (
            <button
              type="button"
              className="clw-btn clw-btn-secondary clw-btn-sm"
              title="Take the uploaded creatives, tags, trackers and pixels — and the placement IDs they assigned — back out of this plan. Saved as a new version; History can restore them."
              disabled={creativeRemove.busy || creativeRemove.disabled}
              onClick={() => creativeRemove.onRemove()}
              data-testid="gt-remove-creatives"
            >
              {creativeRemove.busy ? 'Removing creatives…' : '✕ Remove Creatives'}
            </button>
          )}
          {showAgentButton && (
            <>
              <div className="clw-gtoolbar-sep" />
              <button
                className={`clw-btn clw-btn-ai clw-btn-sm ${aiOpen ? 'active' : ''}`}
                onClick={() => setAiOpen((v) => !v)}
              >✦ Sidekick agent</button>
            </>
          )}
        </div>

        {/* Column group tabs.
            cg-n = number of rows that have ≥ 1 cell with content in this
            group (so the badge tracks the response, not a static column
            count). issue-n = count of cells with annotations in this
            group across all rows — shows a coloured chip when > 0. */}
        <div className="clw-cg-bar">
          {groupKeys.map((g) => {
            const cols = groupCols[g] || [];
            // Rows with at least one populated cell in this tab's columns.
            const populatedRows = reviewRows.filter((r) => cols.some((c) => {
              const v = r[c.k];
              return v != null && v !== '' && v !== '—';
            })).length;
            // Annotated cells in this tab.
            const issueCount = reviewRows.reduce((acc, r) => acc + cols.reduce((a2, c) => (
              (r.annotations || {})[c.k] ? a2 + 1 : a2
            ), 0), 0);
            return (
              <div
                key={g}
                className={`clw-cg-tab ${g === effectiveActiveGroup && !trailingTab?.active ? 'active' : ''}`}
                onClick={() => { setActiveGroup(g); if (trailingTab?.active) trailingTab.onLeave?.(); }}
              >
                {g.charAt(0).toUpperCase() + g.slice(1)}
                <span className="clw-cg-n">{populatedRows}</span>
                {issueCount > 0 && (
                  <span className="clw-cg-issue-n">{issueCount}</span>
                )}
              </div>
            );
          })}
          {/* Breakdown tab — shows the pivot table (DV360/TTD: package /
              tactic / format / month switcher; GT: ad-groups table + QA
              pivot) inside the grid surface instead of the per-column
              line-item view. Special-cased outside groupCols because it
              has its own data shape. */}
          {showBreakdownTab && (
            <div
              className={`clw-cg-tab ${activeGroup === '__breakdown' && !trailingTab?.active ? 'active' : ''}`}
              onClick={() => { setActiveGroup('__breakdown'); if (trailingTab?.active) trailingTab.onLeave?.(); }}
            >
              Breakdown
            </div>
          )}
          {trailingTab && (
            <div
              className={`clw-cg-tab clw-cg-tab-trailing ${trailingTab.active ? 'active' : ''}`}
              data-testid="clw-trailing-tab"
              onClick={() => { if (!trailingTab.active) trailingTab.onSelect?.(); }}
            >
              {trailingTab.label}
            </div>
          )}
        </div>

        <div className="clw-grid-inner" style={parked}>
          <div className="clw-grid-scroll">
            {showBreakdownTab && activeGroup === '__breakdown' ? (
              // Bare identifier, not {breakdownSlot} — this is already inside a
              // JSX expression, so braces here would build an object literal
              // and React would try to render it as a child.
              breakdownSlot
            ) : (
            <table className="clw-gt" style={{ width: f1Width + (stickyWidths.f2 ?? 220) + cols.reduce((s, c) => s + widthOf(c), 0) }}>
              <colgroup>
                <col style={{ width: f1Width }} />
                <col style={{ width: stickyWidths.f2 }} />
                {cols.map((c) => (
                  <col key={c.k} style={{ width: widthOf(c) }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th className="fh1" style={{ width: f1Width }}>
                    {selectionOn && (
                      <input
                        type="checkbox"
                        className="clw-rowsel"
                        checked={allVisibleSelected}
                        // Some-but-not-all is a distinct state: without it, a
                        // partial selection reads as "nothing selected" and the
                        // next click looks like it should be a no-op.
                        ref={(el) => {
                          if (el) el.indeterminate = !allVisibleSelected && visibleSelectedCount > 0;
                        }}
                        onChange={toggleAllVisible}
                        title={allVisibleSelected
                          ? 'Deselect the rows currently shown'
                          : `Select the ${visibleRows.length} row${visibleRows.length === 1 ? '' : 's'} currently shown`}
                      />
                    )}
                    {stickyF1Label}
                    <span
                      className="clw-col-resize"
                      onMouseDown={(e) => startResize(e, 'sticky', 'f1', f1Width)}
                      title="Drag to resize"
                    />
                  </th>
                  <th className="fh2" style={{ width: stickyWidths.f2, left: f2Left }}>
                    {stickyF2Label}
                    <span
                      className="clw-col-resize"
                      onMouseDown={(e) => startResize(e, 'sticky', 'f2', stickyWidths.f2)}
                      title="Drag to resize"
                    />
                  </th>
                  {cols.map((c) => (
                    <th key={c.k} style={{ width: widthOf(c) }}>
                      {c.l}
                      <span
                        className="clw-col-resize"
                        onMouseDown={(e) => startResize(e, 'col', c.k, widthOf(c))}
                        title="Drag to resize"
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={2 + cols.length} style={{ textAlign: 'center', padding: 24, color: 'var(--g4)', fontStyle: 'italic', fontFamily: 'var(--font-serif)' }}>
                      {gridSearch || filterIssuesOnly ? 'No rows match the current filter.' : 'No rows to display.'}
                    </td>
                  </tr>
                )}
                {visibleRows.map((row) => (
                  <tr
                    key={row.rowId}
                    data-clw-rowid={row.rowId}
                    className={isSelected(row.rowId) ? 'clw-row-selected' : ''}
                  >
                    <td className="f1" style={{ width: f1Width }}>
                      {selectionOn && (
                        <input
                          type="checkbox"
                          className="clw-rowsel"
                          checked={isSelected(row.rowId)}
                          // onClick, not onChange, because the shift key is only on
                          // the mouse event — onChange fires without it.
                          onClick={(e) => { e.stopPropagation(); toggleRow(row.rowId, e.shiftKey); }}
                          onChange={() => {}}
                          title="Select this row (shift-click to extend)"
                        />
                      )}
                      {onRemoveRow && !alreadyLaunched ? (
                        armedRemoveRowId === row.rowId ? (
                          <span className="clw-rowdel-confirm">
                            <button
                              type="button"
                              className="clw-rowdel-yes"
                              title="Confirm removal"
                              onClick={(e) => {
                                e.stopPropagation();
                                setArmedRemoveRowId(null);
                                onRemoveRow(row.rowId);
                              }}
                            >✓</button>
                            <button
                              type="button"
                              className="clw-rowdel-no"
                              title="Keep this row"
                              onClick={(e) => { e.stopPropagation(); setArmedRemoveRowId(null); }}
                            >✕</button>
                          </span>
                        ) : (
                          <span className="clw-rowdel-idx">
                            <span className="clw-rowdel-num">{row.idx}</span>
                            <button
                              type="button"
                              className="clw-rowdel-btn"
                              title={removeRowTitle}
                              onClick={(e) => { e.stopPropagation(); setArmedRemoveRowId(row.rowId); }}
                            >×</button>
                          </span>
                        )
                      ) : row.idx}
                    </td>
                    {/* Name is a sticky column, so it can't take the cell-m /
                        cell-q classes — those set position:relative and would
                        break the stickiness. It gets its own tint plus the
                        same marker, wrapped in a relative span, so structural
                        findings ("not found in the DSP") are visible here
                        rather than silently dropped. */}
                    {(() => {
                      const nameAnn = row.annotations?.name
                        && !dismissedAnns.has(`${row.rowId}::name`)
                        ? row.annotations.name : null;
                      return (
                        <td
                          className={`f2${nameAnn ? ` name-ann-${nameAnn.type}` : ''}`}
                          data-clw-cell="name"
                          style={{ width: stickyWidths.f2, left: f2Left }}
                          onClick={() => { setDetailRowId(row.rowId); setDetailCellField('name'); }}
                        >
                          <span className="clw-f2-inner">
                            {getRowValue(row, 'name')}
                            {nameAnn && (
                              <span
                                className={`clw-cell-flag ${nameAnn.type}`}
                                title={nameAnn.msg}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const r = e.currentTarget.getBoundingClientRect();
                                  setFlagPopover({
                                    rowId: row.rowId, field: 'name', ann: nameAnn,
                                    colLabel: 'Name', raw: nameAnn.raw ?? getRowValue(row, 'name'),
                                    x: r.left, y: r.bottom,
                                  });
                                }}
                              >
                                {nameAnn.type === 'm' ? '!' : nameAnn.type === 'q' ? '?' : 'ⓘ'}
                              </span>
                            )}
                          </span>
                        </td>
                      );
                    })()}
                    {cols.map((c) => {
                      const val = getRowValue(row, c.k);
                      // A dismissed annotation stops tinting the cell and drops
                      // its marker, but stays on the row so a re-validate can
                      // surface it again.
                      const rawAnn = row.annotations?.[c.k];
                      const ann = rawAnn && dismissedAnns.has(`${row.rowId}::${c.k}`) ? null : rawAnn;
                      const isEdited = editedRows[row.rowId] && Object.prototype.hasOwnProperty.call(editedRows[row.rowId], c.k);
                      const cls = [
                        ann ? `cell-${ann.type}` : '',
                        isEdited ? 'cell-resolved' : '',
                      ].filter(Boolean).join(' ');

                      // Tactic renders as a coloured code chip + label, the
                      // same component the Campaign Grid uses.
                      if (c.k === 'tactic' && val) {
                        return (
                          <td key={c.k} data-clw-cell={c.k} className={cls} style={{ width: widthOf(c) }} onClick={() => { setDetailRowId(row.rowId); setDetailCellField(c.k); }}>
                            <TacticPill tactic={String(val)} className="clw-tactic-badge" />
                            {ann && (
                              <span className={`clw-cell-flag ${ann.type}`} title={ann.msg}>
                                {ann.type === 'm' ? '!' : ann.type === 'q' ? '?' : 'ⓘ'}
                              </span>
                            )}
                          </td>
                        );
                      }

                      if (c.k === 'fmt') {
                        return (
                          <td key={c.k} data-clw-cell={c.k} className={cls} style={{ width: widthOf(c) }} onClick={() => { setDetailRowId(row.rowId); setDetailCellField(c.k); }}>
                            <span className={`clw-fmt-pill ${fmtBadgeClass(val)}`}>{String(val || '—').toUpperCase()}</span>
                          </td>
                        );
                      }
                      return (
                        <td
                          key={c.k}
                          data-clw-cell={c.k}
                          className={cls}
                          style={{ width: widthOf(c) }}
                          title={String(val ?? '')}
                          onClick={() => {
                            setDetailRowId(row.rowId);
                            setDetailCellField(c.k);
                          }}
                        >
                          {/* An `unread` annotation means the value could not be
                              read at all: leave the cell blank rather than "—",
                              which reads as "there is no value". */}
                          {rawAnn?.unread ? null : fmtCellValue(val, c.fmt, row.currencyCode, row)}
                          {ann && (
                            <span
                              className={`clw-cell-flag ${ann.type}`}
                              title={ann.msg}
                              onClick={(e) => {
                                e.stopPropagation();
                                const r = e.currentTarget.getBoundingClientRect();
                                setFlagPopover({
                                  rowId: row.rowId, field: c.k, ann,
                                  colLabel: c.l, raw: ann.raw ?? val,
                                  x: r.left, y: r.bottom,
                                });
                              }}
                            >
                              {ann.type === 'm' ? '!' : ann.type === 'q' ? '?' : 'ⓘ'}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>

          {/* "Take the rest of the order too?" — opened by checking a single
              row on a grid that passed siblingGroupField. Deliberately a
              blocking Yes/No rather than a toast: the answer changes what the
              next click (✕ Remove selected) does, so it has to be settled
              before the user gets there. Enter = Yes, Escape = No. */}
          {groupPrompt && (
            <>
              <div className="clw-grpsel-scrim" onClick={() => setGroupPrompt(null)} />
              <div
                className="clw-grpsel"
                role="dialog"
                aria-modal="true"
                aria-label={`Select the rest of this ${siblingGroupNoun}`}
                tabIndex={-1}
                // Focus once, so the keyboard shortcuts below are live without
                // stealing focus back on every re-render while it is open.
                ref={(el) => { if (el && !el.contains(document.activeElement)) el.focus(); }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') { e.stopPropagation(); setGroupPrompt(null); }
                  if (e.key === 'Enter') { e.stopPropagation(); acceptGroupPrompt(); }
                }}
              >
                <div className="clw-grpsel-h">
                  Select the rest of this {siblingGroupNoun}?
                </div>
                <div className="clw-grpsel-b">
                  <p>
                    <b>{groupPrompt.rowName || 'This row'}</b> has{' '}
                    <b>{fmtInt(groupPrompt.ids.length)}</b> other{' '}
                    {siblingGroupItemNoun}{groupPrompt.ids.length === 1 ? '' : 's'} in the same{' '}
                    {siblingGroupNoun}
                    {groupPrompt.groupLabel ? <> — <span className="clw-grpsel-io">{groupPrompt.groupLabel}</span></> : null}.
                  </p>
                  {siblingGroupNote && <p className="clw-grpsel-note">{siblingGroupNote}</p>}
                </div>
                <div className="clw-grpsel-f">
                  <button
                    type="button"
                    className="clw-btn clw-btn-navy clw-btn-sm"
                    onClick={acceptGroupPrompt}
                  >Yes — select all {fmtInt(groupPrompt.ids.length + 1)}</button>
                  <button
                    type="button"
                    className="clw-btn clw-btn-secondary clw-btn-sm"
                    onClick={() => setGroupPrompt(null)}
                  >No — just this one</button>
                </div>
              </div>
            </>
          )}

          {/* Flag popover — anchored to the !/? marker. A quick read of what
              the plan supplied and what the validator wants, with a route into
              the full editor or a way to clear an expected difference. */}
          {flagPopover && (
            <>
              <div className="clw-tt-scrim" onClick={() => setFlagPopover(null)} />
              <div
                className="clw-tooltip"
                style={{
                  left: Math.min(flagPopover.x, window.innerWidth - 336),
                  top: Math.min(flagPopover.y + 6, window.innerHeight - 300),
                }}
              >
                <div className="clw-tt-head">
                  <div className="clw-tt-field">{flagPopover.colLabel || flagPopover.field}</div>
                  <span className={`clw-tt-type ${flagPopover.ann.type}`}>
                    {flagPopover.ann.unread ? '! Not read'
                      : flagPopover.ann.type === 'm' ? '! Missing' : flagPopover.ann.type === 'q' ? '? Question' : 'ⓘ Info'}
                  </span>
                </div>
                <div className="clw-tt-body">
                  {/* An unread value has no plan input behind it and nothing to fix here. */}
                  {!flagPopover.ann.unread && (
                    <div className="clw-tt-row">
                      <div className="clw-tt-lbl">Original input from plan</div>
                      <div className="clw-tt-val raw">
                        {flagPopover.raw != null && flagPopover.raw !== '' ? String(flagPopover.raw) : '—'}
                      </div>
                    </div>
                  )}
                  <div className="clw-tt-row">
                    <div className="clw-tt-lbl">{flagPopover.ann.unread ? 'What happened' : <>What&apos;s needed</>}</div>
                    <div className="clw-tt-val">{flagPopover.ann.msg}</div>
                  </div>
                </div>
                <div className="clw-tt-action">
                  <button
                    className="clw-btn clw-btn-navy clw-btn-sm"
                    onClick={() => {
                      setDetailRowId(flagPopover.rowId);
                      setDetailCellField(flagPopover.field);
                      setFlagPopover(null);
                    }}
                  >{flagPopover.ann.unread ? 'Open cell detail' : 'Edit this field'}</button>
                  {/* An unread value is not a finding to accept: its flag is
                      the cell's only sign that the blank is not GT's answer. */}
                  {!flagPopover.ann.unread && (
                    <button
                      className="clw-btn clw-btn-secondary clw-btn-sm"
                      onClick={() => {
                        setDismissedAnns((prev) => new Set(prev).add(`${flagPopover.rowId}::${flagPopover.field}`));
                        setFlagPopover(null);
                      }}
                    >Dismiss</button>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Cell detail panel — full-height slide-over with cell-focused
              content. Shows the clicked column's value and validation
              context, with an "expand to full row" toggle for users who
              want the broader picture. */}
          <CellDetailPanel
            detailRow={detailRow}
            detailCellField={detailCellField}
            isGt={isGt}
            groupCols={groupCols}
            groupKeys={groupKeys}
            getRowValue={getRowValue}
            ctsReview={ctsReview}
            pendingDeletionsForRow={detailRow ? (pendingDeletions[detailRow.rowId] || {}) : {}}
            onToggleListDeletion={toggleListDeletion}
            listFields={listFields}
            pendingListOpsForRow={detailRow ? (pendingListOps[detailRow.rowId] || null) : null}
            onAddListItem={addListItem}
            onEditListItem={editListItem}
            onLookup={onLookup}
            onRefreshLookup={onRefreshLookup}
            onClose={() => { setDetailRowId(null); setDetailCellField(null); }}
            alreadyLaunched={alreadyLaunched}
            editableFields={editableFields}
            fieldEditors={fieldEditors}
            fieldOptions={fieldOptions}
            readOnlyReasons={readOnlyReasons}
            editingCell={editingCell}
            setEditingCell={setEditingCell}
            editingDraft={editingDraft}
            setEditingDraft={setEditingDraft}
            commitCellEdit={commitCellEdit}
            cancelCellEdit={cancelCellEdit}
            editedRows={editedRows}
          />
          {children}
        </div>
        {trailingTab?.active ? null : summaryBar}
      </div>
  );
}
